import { toast } from 'react-toastify';

/**
 * Toast Manager - Prevents duplicate toast messages
 * 
 * Usage:
 * - Use toastManager.error() for error messages that should be debounced
 * - Use toastManager.forceError() for critical warnings that should always show (e.g., "QR code must be unique")
 * - Same pattern applies for success, info, and warning methods
 * 
 * The default debounce time is 30 seconds to prevent spam toasts from API polling.
 */

class ToastManager {
  constructor() {
    // Store recent toasts with their timestamps
    this.recentToasts = new Map();
    // Default debounce time: 30 seconds
    this.debounceTime = 30000;
  }

  // Check if a toast message should be shown
  shouldShowToast(message, type = 'error') {
    const key = `${type}:${message}`;
    const now = Date.now();
    
    if (this.recentToasts.has(key)) {
      const lastShown = this.recentToasts.get(key);
      if (now - lastShown < this.debounceTime) {
        return false; // Don't show duplicate toast
      }
    }
    
    // Update the timestamp for this toast
    this.recentToasts.set(key, now);
    
    // Clean up old entries to prevent memory leaks
    this.cleanupOldToasts();
    
    return true;
  }

  // Clean up toasts older than debounce time
  cleanupOldToasts() {
    const now = Date.now();
    for (const [key, timestamp] of this.recentToasts.entries()) {
      if (now - timestamp > this.debounceTime) {
        this.recentToasts.delete(key);
      }
    }
  }

  // Enhanced toast methods that prevent duplicates
  error(message, options = {}) {
    if (this.shouldShowToast(message, 'error')) {
      return toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        ...options
      });
    }
  }

  success(message, options = {}) {
    if (this.shouldShowToast(message, 'success')) {
      return toast.success(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        ...options
      });
    }
  }

  info(message, options = {}) {
    if (this.shouldShowToast(message, 'info')) {
      return toast.info(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        ...options
      });
    }
  }

  warning(message, options = {}) {
    if (this.shouldShowToast(message, 'warning')) {
      return toast.warning(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        ...options
      });
    }
  }

  // Method to force show a toast (bypass duplicate check)
  forceError(message, options = {}) {
    return toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      ...options
    });
  }

  // Method to force show a success toast (bypass duplicate check)
  forceSuccess(message, options = {}) {
    return toast.success(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      ...options
    });
  }

  // Clear all recent toasts (useful for testing or manual reset)
  clearHistory() {
    this.recentToasts.clear();
  }

  // Set custom debounce time
  setDebounceTime(milliseconds) {
    this.debounceTime = milliseconds;
  }
}

// Create and export a singleton instance
const toastManager = new ToastManager();
export default toastManager; 